import { useMemo } from 'react';
import { useApiData } from './useApiData';
import { selfRegularizationService, throughVendorService } from '../../../../services/apiService';

/**
 * What each record can supply and what a registry has already confirmed, for the whole base in
 * one call (GET /self-regularization/vendor-state). The screens read a Map by vendor id:
 *
 *   { inputs: ['gst_number', 'bank'], malformed: ['pan_number'], verified: Set(['gst']) }
 *
 * No GSTIN, PAN or account number ever reaches the browser — only which of them the record has.
 * `refetch` after an approval, since that changes what counts as verified.
 */
export function useVendorState({ enabled = true, deps = [] } = {}) {
  const api = useApiData(() => selfRegularizationService.vendorState(), deps, { enabled, cacheKey: 'selfreg:vendor-state' });
  const byId = useMemo(() => {
    const m = new Map();
    for (const r of api.data?.data || []) m.set(r.vendor_id, { inputs: r.inputs || [], malformed: r.malformed || [], verified: new Set(r.verified || []) });
    return m;
  }, [api.data]);
  const stateOf = useMemo(() => (v) => byId.get(v.id) || { inputs: [], malformed: [], verified: new Set() }, [byId]);
  return { byId, stateOf, loading: api.loading && !api.data, error: api.error, refetch: api.refetch };
}

/**
 * The newest link per vendor (GET /regularization/links/latest), as a Map by vendor id:
 * { batch_id, status: sent | submitted | verified | expired, at, reminded_at } or undefined.
 */
export function useLatestLinks({ enabled = true, deps = [] } = {}) {
  const api = useApiData(() => throughVendorService.latestLinks(), deps, { enabled, cacheKey: 'reg:latest-links' });
  const byId = useMemo(() => {
    const m = new Map();
    for (const r of api.data?.data || []) m.set(r.vendor_id, r);
    return m;
  }, [api.data]);
  return { byId, linkOf: (id) => byId.get(id) || null, loading: api.loading && !api.data, refetch: api.refetch };
}

/**
 * Who answers the registries, and whether the numbers on screen are real
 * (GET /self-regularization/checks meta). `checks` carries the real prices and availability.
 */
export function useRegistryStatus() {
  const api = useApiData(() => selfRegularizationService.listChecks(), [], { cacheKey: 'selfreg:checks' });
  const meta = api.data?.meta || null;
  return {
    checks: api.data?.data || null,
    provider: meta?.provider || null,
    mock: meta?.provider === 'mock',
    pricesConfigured: meta ? !!meta.prices_configured : true,
    providerProblem: meta?.provider_problem || null,
    loading: api.loading && !api.data,
    refetch: api.refetch,
  };
}
